"use strict";

const { Duration } = require("luxon");

const getNow = require("../utils/get-now");

const timeUnits = [
  {
    name: "minutes",
    singular: () => "This minute",
    plural: (value) => `Last ${value} minutes`
  },
  {
    name: "hours",
    singular: () => "This hour",
    plural: (value) => `Last ${value} hours`
  },
  {
    name: "days",
    singular: () => "Today",
    plural: (value) => `Last ${value} days`
  },
  {
    name: "weeks",
    singular: () => "This week",
    plural: (value) => `Last ${value} weeks`
  },
  {
    name: "months",
    singular: (result) => {
      const { startDate } = result;
      return startDate.toLocaleString({ month: "long" });
    },
    plural: (value) => `Last ${value} months`
  },
  {
    name: "years",
    singular: (result) => {
      const { startDate } = result;
      return startDate.toLocaleString({ year: "numeric" });
    },
    plural: (value) => `Last ${value} years`
  }
];

/**
 * Get format for the provided locale
 */
const getLocaleFormat = ({ hasAssignedTime, rangeWithinThisYear }) => {
  return [
    () => ({ day: "numeric", month: "short" }),
    () => (!rangeWithinThisYear ? { year: "numeric" } : {}),
    () =>
      hasAssignedTime
        ? { hour: "numeric", minute: "numeric", hourCycle: "h23" }
        : {}
  ].reduce((options, fn) => Object.assign(options, fn()), {});
};

/**
 * Get meta data, to optimize the formatting
 */
const getMeta = (startDate, endDate, props) => {
  const now = getNow(props);
  const { mode } = props;

  const isStartDateStartOfDay =
    startDate.valueOf() === startDate.startOf("day").valueOf();
  const isEndDateStartOfDay =
    endDate.valueOf() === endDate.startOf("day").valueOf();
  const hasAssignedTime =
    !isStartDateStartOfDay || (mode === "fixed" && !isEndDateStartOfDay);
  const adjustedEndDate =
    isEndDateStartOfDay && !hasAssignedTime
      ? endDate.minus({ days: 1 })
      : endDate;
  const rangeWithinThisYear =
    startDate.hasSame(now, "year") && adjustedEndDate.hasSame(now, "year");
  const rangeWithinSameDay = startDate.hasSame(adjustedEndDate, "day");

  return {
    isStartDateStartOfDay,
    isEndDateStartOfDay,
    adjustedEndDate,
    hasAssignedTime,
    rangeWithinThisYear,
    rangeWithinSameDay
  };
};

/**
 * Format a date-range or date
 */
const formatDate = (result, props) => {
  const { startDate, endDate } = result;
  const { mode, comparisonRange } = props;

  const meta = getMeta(startDate, endDate, props);
  const localeFormat = getLocaleFormat(meta);
  const formattedStartDate = startDate.toLocaleString(localeFormat);
  const formattedEndDate = meta.adjustedEndDate.toLocaleString(localeFormat);

  if (["since", "rolling"].includes(mode) && !comparisonRange) {
    return `Since ${formattedStartDate}`;
  }

  if (!meta.hasAssignedTime && meta.rangeWithinSameDay) {
    return formattedStartDate;
  }

  return `${formattedStartDate} – ${formattedEndDate}`;
};

/**
 * Format a relative date duration
 *
 * NB:
 * Searches for best fit time-unit based on the dateDuration
 */
const formatRelative = (result, props) => {
  const { dateDuration, snapDates, comparisonRange } = props;
  const durationObject = Duration.fromISO(dateDuration).toObject();
  const bestFitUnit = [...timeUnits]
    .reverse()
    .find((timeUnit) => durationObject[timeUnit.name]);
  const bestFitUnitValue = durationObject[bestFitUnit.name];

  // If the snapDates unit doesn't match the bestFitUnit,
  // fall back to formatting by date
  if (!bestFitUnit.name.includes(snapDates) || !!comparisonRange) {
    return formatDate(result, props);
  }

  if (bestFitUnitValue === 1) {
    return bestFitUnit.singular(result, props);
  }

  return bestFitUnit.plural(bestFitUnitValue, result, props);
};

/**
 * Formats a humanized label for the date-range
 */
const getFormattedLabel = (result, props) => {
  const { mode } = props;

  const label = ["fixed", "since", "single"].includes(mode)
    ? formatDate(result, props)
    : formatRelative(result, props);

  return {
    ...result,
    label
  };
};

module.exports = getFormattedLabel;
